<template>
  <v-container>
    <div v-html="year?.criteria_selection_text"></div>
    <v-form @submit="assignCrit">
      <v-row>
        <v-col>
          <v-text-field
            v-model="pagination.q"
            class="ma-2"
            density="compact"
            placeholder="Search Criteria..."
            hide-details></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-switch
            v-if="!addingOptional && (!teamInitSelect || authStore.isAdmin)"
            :label="hideOpt == false ? 'Show Optional' : 'Hide Optional'"
            :model-value="hideOpt"
            @click="hideOpt = !hideOpt"></v-switch>
          <CriteriaExpansionPanelCompontent
            v-if="criteriaGroups.length > 0"
            :team-id="teamId"
            :criteria-data="criteriaGroups || []"
            :adding-optional="addingOptional"
            :team-criteria-data="selectedCriteria"
            :team-init-select="teamInitSelect"
            :show-save="true"
            @toggleSelection="toggleSelection"
            @save="showConfirmation"></CriteriaExpansionPanelCompontent>
          <div v-else>There are no additional criteria available for this year.</div>
        </v-col>
      </v-row>
      <v-divider></v-divider>

      <v-row>
        <v-col v-if="!addingOptional" cols="12" md="6">
          <FullSecondaryButton @click="clearSelections">Clear</FullSecondaryButton>
        </v-col>
        <v-col v-else cols="12" sm="6" md="6">
          <FullSecondaryButton color="secondary" @click="emit('close')">Close</FullSecondaryButton>
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <FullWidthButton
            v-if="criteriaGroups.length > 0"
            color="primary"
            :loading="isSubmitting"
            :disabled="!isFormValid"
            @click="showConfirmation"
            >Save</FullWidthButton
          >
        </v-col>
      </v-row>
    </v-form>
  </v-container>

  <v-dialog v-model="showModal">
    <v-card>
      <v-card-title>Select Criteria</v-card-title>
      <v-card-text>
        <p>You are about to select your criteria. Once selected you cannot change it.</p>
        <ul>
          <li v-for="crit in selectedCriteria" :key="crit.criteria?.id">
            {{ crit.criteria?.name }}
          </li>
        </ul>
      </v-card-text>
      <v-card-actions>
        <v-btn color="secondary" @click="showModal = false">Cancel</v-btn>
        <v-btn color="primary" @click="assignCrit">Select</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script setup lang="ts">
import {
  useAuthStore,
  useCriteriaStore,
  useSubmissionnStore,
  useUpdateStatusStore,
  useYearStore,
} from '@/stores';
import { computed, ref, watch } from 'vue';
import { SNACKSTATUS } from '@/stores/util';
import {
  BranchYearDTO,
  ICriteriaDTO,
  IPaginationDTO,
  ITeamCriteriaSelectionDTO,
  PaginationDirectionEnum,
  PaginationDTO,
  TeamCriteriaSelectionDTO,
} from '@/dtos';
import CriteriaExpansionPanelCompontent from './CriteriaExpansionPanelCompontent.vue';

interface Props {
  yearId: string;
  branchYear: BranchYearDTO;
  teamId: string;
  teamInitSelect?: boolean;
  teamLevelId?: string;
  addingOptional?: boolean;
  usedCriteria?: ICriteriaDTO[];
}
const props = defineProps<Props>();
const branchYear = computed(() => props.branchYear);
const teamId = computed(() => props.teamId);
const yearId = computed(() => props.yearId);
const teamLevelId = computed(() => props.teamLevelId ?? '');
const teamInitSelect = computed(() => props.teamInitSelect ?? false);
const addingOptional = computed(() => props.addingOptional ?? false);
const showModal = ref<boolean>(false);

const authStore = useAuthStore();
const selectedCriteria = ref<ITeamCriteriaSelectionDTO[]>([]);
const emit = defineEmits(['snack', 'clearSelection', 'close']);
const criteriaStore = useCriteriaStore();
const submissionStore = useSubmissionnStore();
const statusStore = useUpdateStatusStore();
const yearStore = useYearStore();

const year = computed(() =>
  yearStore.selectedYear.value ? yearStore.selectedYear.value : yearStore.currentYear,
);

const branchYearId = computed(() => branchYear.value.id);
const hideOpt = ref<boolean>(true);
let page = new PaginationDTO();
page.orderBy = 'criterias.number';
page.orderDirection = PaginationDirectionEnum.ASC;

const pagination = ref<IPaginationDTO>(page);

const { data: teamCriteriaData } = criteriaStore.getTeamsCriteriaQuery(yearId, teamId);
const { data: criteriaData } = criteriaStore.getCriteriaForBranchYearQuery(
  branchYearId,
  pagination,
);
const { data: levelCount } = criteriaStore.getCriteriaCountForTeamLevelYear(yearId, teamLevelId);
const criteriaGroups = computed(() => {
  const usedIds = new Set(props.usedCriteria?.map((used) => used.id));
  const data =
    criteriaData.value?.data
      .map((group) => {
        const filteredCriterias = group.criterias.filter((criteria) => {
          if (usedIds.has(criteria.id)) {
            return false;
          }
          return true;
        });

        return {
          ...group,
          criterias: filteredCriterias,
        };
      })
      .filter((group) => group.criterias.length > 0) ?? [];

  return data;
});

const maxManditory = computed(() => levelCount.value?.mandatory_number ?? 0);
const isSubmitting = ref<boolean>(false);
let isFormValid = computed(() => {
  return teamId.value != null && teamId.value != '';
});

const criterias = ref<ITeamCriteriaSelectionDTO[]>([]);

const showConfirmation = () => {
  if (addingOptional.value == false && authStore.isUser && teamInitSelect) {
    checkIfGood();
    return;
  }
  assignCrit();
};

const assignCrit = () => {
  if (addingOptional.value) {
    submissionStore.addOptionalCriteria.mutateAsync(
      selectedCriteria.value.map((crit) => crit.criteria?.id ?? ''),
    );
    // emit('close');
  } else {
    criteriaStore.assignTeamCriteria.mutateAsync({
      yearId: yearId.value,
      teamId: teamId.value,
      criterias: selectedCriteria.value,
    });
  }
};

const checkIfGood = () => {
  if (authStore.isUser) {
    if (selectedCriteria.value.length > maxManditory.value) {
      emit('snack', 'Critieria', 'error', 'too many selected');
      return;
    }
    if (selectedCriteria.value.length < maxManditory.value) {
      emit('snack', 'Critieria', 'error', 'too few selected');
      return;
    }
  }
  showModal.value = true;
};

const clearSelections = () => {
  emit('clearSelection');
  selectedCriteria.value = [];
};

const key = computed(() => `assign-criteria-${teamId.value}-${yearId.value}`);

watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value || addingOptional.value == true) {
      return;
    }

    emit('snack', 'Team Criteria', value, statusStore.getError(key.value));
    if (teamId.value && value && value == 'success:update') {
      //TODO: ENABLE
      // sendCheckinToDiscord(selectedTeam.value?.name)
    }
    //clearSelections();
    showModal.value = false;
  },
);

watch(teamCriteriaData, () => {
  if (teamCriteriaData.value?.data) {
    selectedCriteria.value = teamCriteriaData.value.data.map(
      (selected) => new TeamCriteriaSelectionDTO(selected),
    );
  }
});

watch(criteriaData, () => {
  if (criteriaData.value?.data) {
    criterias.value = criteriaData.value.data.map(
      (selected) => new TeamCriteriaSelectionDTO(selected),
    );
  }
});

const toggleSelection = (criteria: ICriteriaDTO, isMan?: boolean) => {
  const index = selectedCriteria.value.findIndex(
    (selected) => selected.criteria?.id === criteria.id,
  );
  if (index > -1) {
    selectedCriteria.value.splice(index, 1);
  } else {
    selectedCriteria.value.push(
      new TeamCriteriaSelectionDTO({ criteria: criteria, mandatory: isMan }),
    );
  }
};
</script>
